import React, { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CheckCircle2, AlertCircle, FileText, Loader2, Send, RefreshCw } from "lucide-react";
import { useLoader } from "@/contexts/LoaderContext";
import { useJourney } from "@/contexts/JourneyContext";
import { toast } from "sonner";
import axios from 'axios';
import { API_BASE_URL } from '@/config';
import JourneySectionHeader from "@/components/JourneySectionHeader";
import { JourneyProgressSection } from "@/apps/los/components/ProgressBar/JourneySectionProgress";

interface CaprionESignProps {
    isBuilderMode?: boolean;
    onNext?: () => Promise<void>;
    /** Passed by JourneyPage to every fullPageComponent — drives the header progress bar. */
    progressSections?: JourneyProgressSection[];
    currentPageId?: number | null;
}

type SigningStatus = 'idle' | 'sending_otp' | 'otp_sent' | 'verifying' | 'completed' | 'failed';

const CaprionESign: React.FC<CaprionESignProps> = ({
    isBuilderMode = false,
    onNext,
    progressSections,
    currentPageId,
}) => {
    const { setLoading } = useLoader();
    const { refresh: refreshJourney } = useJourney();
    
    const [status, setStatus] = useState<SigningStatus>('idle');
    const [otp, setOtp] = useState('');
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [countdown, setCountdown] = useState(0);

    // Countdown timer for resend OTP
    useEffect(() => {
        if (countdown > 0) {
            const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
            return () => clearTimeout(timer);
        }
    }, [countdown]);

    const sendOTP = async () => {
        setLoading(true);
        setStatus('sending_otp');
        setErrorMessage(null);

        try {
            const response = await axios.post(`${API_BASE_URL}/api/caprion/send-agreement-otp/`);
            
            if (response.data.success) {
                setStatus('otp_sent');
                setCountdown(30); // 30 second cooldown for resend
                toast.success(response.data.message || "OTP sent to your registered mobile number");
            } else {
                throw new Error(response.data.error || "Failed to send OTP");
            }
        } catch (error: any) {
            console.error("Send OTP Error:", error);
            setStatus('failed');
            setErrorMessage(error.response?.data?.error || error.message || "Failed to send OTP");
            toast.error("Could not send OTP. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const verifyOTP = async () => {
        if (!otp || otp.length !== 6) {
            toast.error("Please enter a valid 6-digit OTP");
            return;
        }

        if (!/^\d{6}$/.test(otp)) {
            toast.error("OTP must contain only numbers");
            return;
        }

        setLoading(true);
        setStatus('verifying');
        setErrorMessage(null);

        try {
            const response = await axios.post(`${API_BASE_URL}/api/caprion/verify-agreement-otp/`, {
                otp: otp
            });
            
            if (response.data.success) {
                setStatus('completed');
                toast.success("Agreement signed successfully!");
                
                // Wait a moment to show success state
                setTimeout(async () => {
                    // Advance journey using onNext prop if available
                    if (onNext) {
                        await onNext();
                    } else {
                        await refreshJourney();
                    }
                }, 1500);
            } else {
                throw new Error(response.data.error || "Invalid OTP");
            }
        } catch (error: any) {
            console.error("Verify OTP Error:", error);
            setStatus('otp_sent'); // Go back to OTP entry
            setErrorMessage(error.response?.data?.error || error.message || "Invalid OTP");
            toast.error("OTP verification failed. Please try again.");
            setOtp(''); // Clear OTP input
        } finally {
            setLoading(false);
        }
    };

    const handleResendOTP = () => {
        if (countdown > 0) {
            toast.error(`Please wait ${countdown} seconds before resending`);
            return;
        }
        setOtp(''); // Clear previous OTP
        sendOTP();
    };

    const handleOtpChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value.replace(/\D/g, ''); // Only allow digits
        if (value.length <= 6) {
            setOtp(value);
        }
    };

    const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter' && otp.length === 6) {
            verifyOTP();
        }
    };

    if (isBuilderMode) {
        return (
            <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 p-4 text-center text-sm text-gray-400">
                <div className="text-2xl mb-1">✍️</div>
                <p className="font-semibold text-gray-500">Caprion Agreement Signing</p>
                <p className="text-xs mt-1">OTP-based agreement signing flow</p>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 flex flex-col">
            {/* Header — page title + journey progress, same as the Personal/Business
                Information pages. The card below used to carry the logo itself. */}
            <JourneySectionHeader
                className="shrink-0"
                title="Sign Loan Agreement"
                progressSections={progressSections}
                currentPageId={currentPageId}
            />

            <div className="flex-1 flex flex-col items-center justify-center p-4">
            <div className="max-w-md w-full bg-white rounded-2xl shadow-xl border p-8 text-center">
                {/* Idle / Initial State */}
                {status === 'idle' && (
                    <>
                        <div className="w-16 h-16 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                            <FileText className="w-8 h-8" />
                        </div>
                        <h1 className="text-2xl font-bold text-gray-900 mb-2">Sign Loan Agreement</h1>
                        <p className="text-gray-500 mb-8">
                            We'll send an OTP to your registered mobile number to complete the agreement signing.
                        </p>
                        <Button
                            onClick={sendOTP}
                            className="w-full bg-intellend-green hover:bg-intellend-green/90 h-12 text-lg font-bold"
                        >
                            <Send className="mr-2 h-5 w-5" />
                            Send OTP
                        </Button>
                    </>
                )}

                {/* Sending OTP */}
                {status === 'sending_otp' && (
                    <div className="py-12">
                        <Loader2 className="w-12 h-12 text-intellend-green animate-spin mx-auto mb-4" />
                        <h2 className="text-xl font-semibold text-gray-900 mb-2">Sending OTP</h2>
                        <p className="text-gray-500">Please wait...</p>
                    </div>
                )}

                {/* OTP Sent - Enter OTP */}
                {status === 'otp_sent' && (
                    <>
                        <div className="w-16 h-16 bg-intellend-green/10 text-intellend-green rounded-full flex items-center justify-center mx-auto mb-4">
                            <Send className="w-8 h-8" />
                        </div>
                        <h2 className="text-2xl font-bold text-gray-900 mb-2">OTP Sent!</h2>
                        <p className="text-gray-500 mb-6">
                            Please enter the 6-digit OTP sent to your registered mobile number
                        </p>

                        {errorMessage && (
                            <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
                                <p className="text-sm text-red-600">{errorMessage}</p>
                            </div>
                        )}

                        <div className="mb-6">
                            <Input
                                type="text"
                                inputMode="numeric"
                                pattern="\d{6}"
                                maxLength={6}
                                value={otp}
                                onChange={handleOtpChange}
                                onKeyPress={handleKeyPress}
                                placeholder="Enter 6-digit OTP"
                                className="text-center text-2xl font-bold tracking-widest h-14"
                                autoFocus
                            />
                        </div>

                        <Button
                            onClick={verifyOTP}
                            disabled={otp.length !== 6}
                            className="w-full bg-intellend-green hover:bg-intellend-green/90 h-12 text-lg font-bold mb-4"
                        >
                            <CheckCircle2 className="mr-2 h-5 w-5" />
                            Verify & Sign
                        </Button>

                        <div className="text-center">
                            <button
                                onClick={handleResendOTP}
                                disabled={countdown > 0}
                                className={`text-sm ${
                                    countdown > 0
                                        ? 'text-gray-400 cursor-not-allowed'
                                        : 'text-intellend-green hover:underline'
                                }`}
                            >
                                {countdown > 0 ? (
                                    `Resend OTP in ${countdown}s`
                                ) : (
                                    <>
                                        <RefreshCw className="inline w-4 h-4 mr-1" />
                                        Resend OTP
                                    </>
                                )}
                            </button>
                        </div>
                    </>
                )}

                {/* Verifying OTP */}
                {status === 'verifying' && (
                    <div className="py-12">
                        <Loader2 className="w-12 h-12 text-intellend-green animate-spin mx-auto mb-4" />
                        <h2 className="text-xl font-semibold text-gray-900 mb-2">Verifying OTP</h2>
                        <p className="text-gray-500">Please wait while we verify your OTP...</p>
                    </div>
                )}

                {/* Completed */}
                {status === 'completed' && (
                    <div className="py-8">
                        <div className="w-16 h-16 bg-intellend-green/10 text-intellend-green rounded-full flex items-center justify-center mx-auto mb-4">
                            <CheckCircle2 className="w-8 h-8" />
                        </div>
                        <h2 className="text-2xl font-bold text-gray-900 mb-2">Successfully Signed!</h2>
                        <p className="text-gray-500 mb-6 font-medium">
                            Your loan agreement has been digitally signed.
                        </p>
                        <Loader2 className="w-6 h-6 text-intellend-green animate-spin mx-auto" />
                        <p className="text-sm text-gray-400 mt-2">Proceeding to next step...</p>
                    </div>
                )}

                {/* Failed */}
                {status === 'failed' && (
                    <div className="py-8">
                        <div className="w-16 h-16 bg-red-100 text-red-600 rounded-full flex items-center justify-center mx-auto mb-4">
                            <AlertCircle className="w-8 h-8" />
                        </div>
                        <h2 className="text-2xl font-bold text-gray-900 mb-2">Failed to Send OTP</h2>
                        <p className="text-gray-500 mb-6">{errorMessage || "An error occurred. Please try again."}</p>
                        <Button
                            onClick={() => setStatus('idle')}
                            variant="outline"
                            className="w-full"
                        >
                            Try Again
                        </Button>
                    </div>
                )}
            </div>

            {/* Help Text */}
            {(status === 'otp_sent' || status === 'idle') && (
                <div className="mt-6 max-w-md w-full text-center">
                    <p className="text-sm text-gray-500">
                        Having trouble? Contact support for assistance
                    </p>
                </div>
            )}
            </div>
        </div>
    );
};

export default CaprionESign;
